import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { PlatformService } from '@panamax/app-state';
import { DsmInfo } from '@usf/ngrx-order/lib/models/client/dsm-info';

export interface TerritorySelectionResult {
  wasCancelled?: boolean;
  selectedTerritories?: DsmInfo[];
}

@Component({
  selector: 'app-territory-selection',
  templateUrl: './territory-selection.component.html',
  styleUrls: ['./territory-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TerritorySelectionComponent {
  @Input() dsmInfo: DsmInfo[];
  @Input() preSelectedTerritories: DsmInfo[];

  selectedTerritories: Set<number> = new Set();

  private platformType = this.platformService.platformEnum;

  constructor(
    private modalController: ModalController,
    protected platformService: PlatformService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  get isDesktop(): boolean {
    return this.platformService.getPlatformType() === this.platformType.desktop;
  }

  get isTablet(): boolean {
    return this.platformService.getPlatformType() === this.platformType.tablet;
  }

  get isMobile(): boolean {
    return this.platformService.getPlatformType() === this.platformType.mobile;
  }

  get allSelected(): boolean {
    return this.selectedTerritories.size === this.dsmInfo.length;
  }

  ionViewWillEnter() {
    this.findPreSelections();
  }

  selectTerritory(index: number) {
    if (this.selectedTerritories.has(index)) {
      this.selectedTerritories.delete(index);
    } else {
      this.selectedTerritories.add(index);
    }
  }

  toggleSelectAll() {
    if (this.selectedTerritories.size === this.dsmInfo.length) {
      this.selectedTerritories.clear();
    } else {
      this.dsmInfo.forEach((_, index) => this.selectedTerritories.add(index));
    }
  }

  formatCustomerName(territory: DsmInfo) {
    return [territory.firstName, territory.lastName]
      .filter(name => !!name)
      .join(' ');
  }

  formatRouteNumbers(territory: DsmInfo) {
    return territory.salesRoutes.map(route => route.salesRoute).join(', ');
  }

  close() {
    this.dismissSelf({
      wasCancelled: true,
    });
  }

  saveTerritorySelection() {
    this.dismissSelf({
      selectedTerritories: this.getSelectedTerritories(),
    });
  }

  private findPreSelections() {
    if (!this.preSelectedTerritories?.length || !this.dsmInfo?.length) {
      return;
    }

    const makeKey = (territory: DsmInfo) =>
      `${territory.userName}${territory.email}`;

    const dsmKeys = this.dsmInfo.map(territory => makeKey(territory));
    this.preSelectedTerritories.forEach(territory => {
      const index = dsmKeys.indexOf(makeKey(territory));
      this.selectedTerritories.add(index);
    });

    this.changeDetectorRef.detectChanges();
  }

  private dismissSelf(result: TerritorySelectionResult) {
    this.modalController.dismiss(result);
  }

  private getSelectedTerritories(): DsmInfo[] {
    const userSelections: DsmInfo[] = [];

    this.selectedTerritories.forEach(index => {
      userSelections.push(this.dsmInfo[index]);
    });

    return userSelections;
  }
}
